import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { AnimatePresence, motion } from 'framer-motion'
import { CaretDown, X } from '@phosphor-icons/react'
import { cn } from '@/lib/utils'

interface Option {
  value: string
  label: string
}

interface MultiSelectCheckboxProps {
  options: Option[]
  selected: string[]
  onChange: (values: string[]) => void
  placeholder?: string
  colorClass?: string
}

/** Max height of the portalled option list, in px (matches Select's default). */
const MENU_MAX_HEIGHT = 288

/**
 * Multi-select checkbox dropdown. Menu is portal-rendered to `document.body`
 * with fixed positioning (§19) so it escapes every `overflow-hidden` /
 * `overflow-y-auto` ancestor — `NodeConfigPanel`'s scroll body used to clip it.
 * Positioning, dismissal and the portal block mirror `Select.tsx`, the kit's
 * reference implementation; only the keyboard contract differs, because
 * toggling an option here must NOT close the menu.
 */
export default function MultiSelectCheckbox({
  options,
  selected,
  onChange,
  placeholder = 'Any',
  // Canonical brand tint. An off-palette hue (emerald/amber) has no dark-mode
  // partner and reads as a status colour it is not.
  colorClass = 'bg-[#5e6ad2]/15 text-[#5e6ad2] dark:text-[#a5b4fc]',
}: MultiSelectCheckboxProps) {
  const [isOpen, setIsOpen] = useState(false)
  const [activeIndex, setActiveIndex] = useState(-1)
  const [menuStyle, setMenuStyle] = useState<React.CSSProperties | null>(null)
  const containerRef = useRef<HTMLDivElement>(null)
  const triggerRef = useRef<HTMLButtonElement>(null)
  const menuRef = useRef<HTMLUListElement>(null)

  // Stable id base so the trigger can point `aria-activedescendant` at the
  // highlighted option — that is what makes arrow-key nav announce.
  const uid = React.useId()
  const listboxId = `${uid}listbox`
  const optionId = useCallback((index: number) => `${uid}opt-${index}`, [uid])

  const close = useCallback(() => {
    setIsOpen(false)
    setActiveIndex(-1)
  }, [])

  const closeAndRefocus = useCallback(() => {
    close()
    triggerRef.current?.focus()
  }, [close])

  // Opening is the ONLY moment the active index is seeded — seeding it from an
  // effect keyed on `[isOpen, options, selected]` would re-fire on every render
  // while open and stomp the highlight (and `aria-activedescendant`) back mid
  // arrow-key nav. `seed` picks the fallback when nothing is checked yet.
  const openMenu = useCallback(
    (seed: 'selected' | 'first' | 'last' = 'selected') => {
      const selectedIdx = options.findIndex((o) => selected.includes(o.value))
      const fallback = seed === 'first' ? 0 : seed === 'last' ? options.length - 1 : -1
      setActiveIndex(Math.min(selectedIdx >= 0 ? selectedIdx : fallback, options.length - 1))
      setIsOpen(true)
    },
    [options, selected],
  )

  const computeMenuStyle = useCallback((): React.CSSProperties | null => {
    const rect = triggerRef.current?.getBoundingClientRect()
    if (!rect) return null
    const margin = 6
    const viewportH = window.innerHeight
    const viewportW = window.innerWidth
    const spaceBelow = viewportH - rect.bottom - margin
    const spaceAbove = rect.top - margin
    // Open upward only when below is cramped AND above has more room.
    const openUp = spaceBelow < Math.min(MENU_MAX_HEIGHT, 200) && spaceAbove > spaceBelow
    // Clamp to the room on the chosen side so the list never runs off the top
    // or bottom of the viewport.
    const available = Math.max(0, openUp ? spaceAbove : spaceBelow)
    const maxHeight = Math.min(MENU_MAX_HEIGHT, available)
    // Keep the menu inside the viewport horizontally.
    const left = Math.max(margin, Math.min(rect.left, viewportW - rect.width - margin))
    const base: React.CSSProperties = {
      position: 'fixed',
      left,
      width: rect.width,
      maxHeight,
      zIndex: 1000,
    }
    // Anchor the BOTTOM edge when opening up so a short list hugs the trigger
    // instead of floating detached above it.
    if (openUp) {
      base.bottom = viewportH - rect.top + margin
    } else {
      base.top = rect.bottom + margin
    }
    return base
  }, [])

  // Position once on open, then DISMISS on scroll + resize (§54). The menu is
  // fixed to the viewport and never chases its trigger, so letting the page
  // scroll under it would leave a menu anchored to nothing.
  //
  // Capture phase because `scroll` does not bubble — that is what catches
  // scrolling inside nested ancestor scrollers (NodeConfigPanel's body). The
  // `menuRef` guard is mandatory: without it, scrolling the option list itself
  // would close the menu on the first wheel tick.
  useLayoutEffect(() => {
    if (!isOpen) {
      setMenuStyle(null)
      return
    }
    setMenuStyle(computeMenuStyle())
    const onScroll = (e: Event) => {
      if (menuRef.current?.contains(e.target as Node)) return
      close()
    }
    window.addEventListener('scroll', onScroll, true)
    window.addEventListener('resize', close)
    return () => {
      window.removeEventListener('scroll', onScroll, true)
      window.removeEventListener('resize', close)
    }
  }, [isOpen, computeMenuStyle, close])

  // Outside click — checks the trigger container (chips included, so removing a
  // chip does not dismiss) AND the portalled menu, which is not a DOM
  // descendant of the container.
  useEffect(() => {
    if (!isOpen) return
    const handler = (e: MouseEvent) => {
      const target = e.target as Node
      if (containerRef.current?.contains(target)) return
      if (menuRef.current?.contains(target)) return
      close()
    }
    window.addEventListener('mousedown', handler)
    return () => window.removeEventListener('mousedown', handler)
  }, [isOpen, close])

  const toggle = useCallback(
    (value: string) => {
      if (selected.includes(value)) {
        onChange(selected.filter((v) => v !== value))
      } else {
        onChange([...selected, value])
      }
    },
    [onChange, selected],
  )

  // Keyboard nav while open. Unlike Select, Enter/Space TOGGLES and leaves the
  // menu open — picking several is the entire point of the control. Only
  // Escape, outside click, scroll or resize dismiss.
  //
  // `preventDefault()` on Enter/Space is load-bearing, not cosmetic: focus
  // stays on the trigger `<button>` while the menu is open, so without it the
  // browser would also synthesise a click on the trigger and close the menu on
  // the same keystroke that toggled the option (§57's ConfirmDialog trap).
  useEffect(() => {
    if (!isOpen) return
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault()
        closeAndRefocus()
        return
      }
      if (e.key === 'ArrowDown') {
        e.preventDefault()
        setActiveIndex((i) => Math.min(options.length - 1, (i < 0 ? -1 : i) + 1))
        return
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault()
        setActiveIndex((i) => Math.max(0, (i < 0 ? options.length : i) - 1))
        return
      }
      if (e.key === 'Home') {
        e.preventDefault()
        setActiveIndex(0)
        return
      }
      if (e.key === 'End') {
        e.preventDefault()
        setActiveIndex(options.length - 1)
        return
      }
      if ((e.key === 'Enter' || e.key === ' ') && activeIndex >= 0) {
        e.preventDefault()
        const opt = options[activeIndex]
        if (opt) toggle(opt.value)
      }
    }
    window.addEventListener('keydown', handler)
    return () => window.removeEventListener('keydown', handler)
  }, [isOpen, activeIndex, options, toggle, closeAndRefocus])

  // Keep the active option inside the menu's own scroller — `maxHeight` clamps
  // the list, so arrow-keying past the visible window would otherwise move the
  // highlight (and `aria-activedescendant`) somewhere unseen. Safe against §54:
  // the scroll-dismiss handler early-returns for targets inside `menuRef`, and
  // the scrolled node is a descendant of it.
  //
  // `menuStyle` is a dependency, not just a guard: the `<ul>` only mounts on the
  // commit that sets it.
  useEffect(() => {
    if (!isOpen || !menuStyle || activeIndex < 0) return
    const el = document.getElementById(optionId(activeIndex))
    if (el && menuRef.current?.contains(el)) el.scrollIntoView({ block: 'nearest' })
  }, [isOpen, menuStyle, activeIndex, optionId])

  const remove = (value: string) => {
    onChange(selected.filter((v) => v !== value))
  }

  const getLabel = (value: string) => {
    return options.find((o) => o.value === value)?.label || value
  }

  return (
    <div ref={containerRef} className="relative">
      {/* Selected chips */}
      {selected.length > 0 && (
        <div className="flex flex-wrap gap-1 mb-1.5">
          {selected.map(val => (
            <span
              key={val}
              className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${colorClass}`}
            >
              {getLabel(val)}
              <button
                type="button"
                onClick={() => remove(val)}
                aria-label={`Remove ${getLabel(val)}`}
                className="text-muted-foreground hover:text-foreground dark:text-[#8a8f98] dark:hover:text-[#f7f8f8] ml-0.5 transition-colors"
              >
                <X className="w-3 h-3" />
              </button>
            </span>
          ))}
        </div>
      )}

      {/* Toggle button */}
      <button
        ref={triggerRef}
        type="button"
        role="combobox"
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        aria-controls={isOpen ? listboxId : undefined}
        aria-activedescendant={isOpen && activeIndex >= 0 ? optionId(activeIndex) : undefined}
        onClick={() => {
          if (isOpen) close()
          else openMenu('selected')
        }}
        // A closed trigger must open on ArrowDown / ArrowUp / Enter / Space
        // (ARIA select-only combobox). Bail while open so this and the window
        // listener above never both handle the same key.
        onKeyDown={(e) => {
          if (isOpen) return
          if (e.key === 'ArrowDown') {
            e.preventDefault()
            openMenu('first')
          } else if (e.key === 'ArrowUp') {
            e.preventDefault()
            openMenu('last')
          } else if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault()
            openMenu('selected')
          }
        }}
        className={cn(
          // R1 (§53): one height, one radius — h-9 / rounded-md, same as Select.
          'w-full flex items-center justify-between gap-2 h-9 px-3 rounded-md border transition-colors duration-150',
          'bg-background border-border/60 text-foreground',
          'hover:border-border/80 hover:bg-background',
          'focus:outline-none focus-visible:border-[#7170ff]/55',
          'dark:bg-white/[0.02] dark:border-white/[0.08] dark:text-[#f7f8f8]',
          'dark:hover:bg-white/[0.03] dark:hover:border-white/[0.14]',
          'dark:focus-visible:border-[#7170ff]/75',
          isOpen && 'border-[#7170ff]/55 dark:border-[#7170ff]/75',
        )}
        style={{ fontSize: '13px', fontWeight: 510, letterSpacing: '-0.13px' }}
      >
        <span
          className={cn(
            'truncate',
            selected.length === 0
              ? 'text-muted-foreground dark:text-[#62666d]'
              : 'text-foreground dark:text-[#f7f8f8]',
          )}
        >
          {selected.length === 0 ? placeholder : `${selected.length} selected`}
        </span>
        <CaretDown
          className={cn(
            'w-3.5 h-3.5 flex-shrink-0 text-muted-foreground dark:text-[#8a8f98] transition-transform duration-150',
            isOpen && 'rotate-180',
          )}
        />
      </button>

      {/* Checkbox dropdown — portalled (§19), dismissed on scroll (§54) */}
      {createPortal(
        <AnimatePresence>
          {isOpen && menuStyle && (
            <motion.div
              initial={{ opacity: 0, y: menuStyle.bottom != null ? 4 : -4, scale: 0.98 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: menuStyle.bottom != null ? 4 : -4, scale: 0.98 }}
              transition={{ duration: 0.15, ease: [0.16, 1, 0.3, 1] }}
              style={menuStyle}
              className={menuStyle.bottom != null ? 'origin-bottom' : 'origin-top'}
            >
              <ul
                ref={menuRef}
                id={listboxId}
                role="listbox"
                aria-multiselectable="true"
                tabIndex={-1}
                style={{ maxHeight: menuStyle.maxHeight }}
                className={cn(
                  'overflow-y-auto overflow-x-hidden rounded-lg',
                  'bg-card border border-border/60 shadow-lg',
                  'dark:bg-[#191a1b] dark:border-white/[0.08]',
                  'dark:shadow-[0_12px_32px_rgba(0,0,0,0.55),0_2px_6px_rgba(0,0,0,0.35),inset_0_0_0_1px_rgba(255,255,255,0.02)]',
                )}
              >
                {options.map((option, i) => {
                  const isSelected = selected.includes(option.value)
                  const isActive = activeIndex === i
                  return (
                    // One node, one role (§57): the `<li role="option">` IS the
                    // interactive element. The checkbox inside is a *visual*
                    // affordance only — `aria-hidden` + `tabIndex={-1}` +
                    // `pointer-events-none` + `readOnly` keep it out of the
                    // a11y tree and out of the focus order, so `role="option"`
                    // never contains a focusable control. `accent-[#5e6ad2]`
                    // survives, which a hand-rolled span could not guarantee.
                    <li
                      key={option.value}
                      id={optionId(i)}
                      role="option"
                      aria-selected={isSelected}
                      onClick={() => toggle(option.value)}
                      onMouseEnter={() => setActiveIndex(i)}
                      // Keep focus on the trigger when an option is clicked.
                      // Options are deliberately not focusable, so a plain
                      // mousedown would blur the trigger to <body> — which
                      // silently kills `aria-activedescendant` announcements
                      // and the window key handlers. Scoped to the row, not the
                      // <ul>, so dragging the menu's own scrollbar still works.
                      onMouseDown={(e) => e.preventDefault()}
                      className={cn(
                        'flex items-center gap-2.5 px-3 py-2 cursor-pointer transition-colors',
                        isActive
                          ? 'bg-[#5e6ad2]/[0.10] dark:bg-white/[0.08]'
                          : isSelected
                            ? 'bg-[#5e6ad2]/[0.06] dark:bg-white/[0.06]'
                            : 'hover:bg-accent/30 dark:hover:bg-white/[0.04]',
                      )}
                    >
                      <input
                        type="checkbox"
                        checked={isSelected}
                        readOnly
                        aria-hidden="true"
                        tabIndex={-1}
                        className="w-3.5 h-3.5 rounded-sm border-border/60 dark:border-white/[0.12] accent-[#5e6ad2] pointer-events-none"
                      />
                      <span
                        className={cn(
                          isSelected
                            ? 'text-[#5e6ad2] dark:text-[#a5b4fc] font-medium'
                            : 'text-foreground dark:text-[#f7f8f8]',
                        )}
                        style={{ fontSize: '13px', fontWeight: 510, letterSpacing: '-0.13px' }}
                      >
                        {option.label}
                      </span>
                    </li>
                  )
                })}
              </ul>
            </motion.div>
          )}
        </AnimatePresence>,
        document.body,
      )}
    </div>
  )
}
